import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar, ChartBar, Eye, ShoppingBag, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { 
  startOfDay, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfDay, 
  format, 
  eachDayOfInterval,
  getDaysInMonth,
  parseISO,
  isToday,
  formatISO,
  addDays,
  subDays
} from "date-fns";
import { cn } from "@/lib/utils";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { getTotalPageViews, getWeeklyPageViews } from "@/utils/analyticsUtils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatCurrency } from "@/utils/analyticsUtils";
import { fetchTopRequestedServices } from "@/utils/serviceUtils";
import { useTranslation } from "react-i18next";

type TimePeriod = 'today' | 'week' | 'month';
type ChartData = { date: string; fullDate: string; views: number; requests: number; revenue: number; key: string }[];

export const BasicPlanSection = () => {
  const { t } = useTranslation();
  const [viewCount, setViewCount] = useState(0);
  const [requestCount, setRequestCount] = useState(0);
  const [salesAmount, setSalesAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('week');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [chartData, setChartData] = useState<ChartData>([]);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [hasRevenueData, setHasRevenueData] = useState(false);
  const [userCurrency, setUserCurrency] = useState<string>("KES");
  const [topServices, setTopServices] = useState<any[]>([]);
  const [loadingTopServices, setLoadingTopServices] = useState(true);

  const gebeya = {
    pink: "#FF1493",
    orange: "#FFA500",
    purple: "#181326"
  };

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
        
        const { data: profileData, error } = await supabase
          .from('profiles')
          .select('currency')
          .eq('id', user.id)
          .single();
          
        if (!error && profileData && profileData.currency) {
          setUserCurrency(profileData.currency);
        }
      }
    };
    
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUserId) {
      fetchData(selectedPeriod);
      loadTopServices();
    }
  }, [selectedPeriod, currentUserId]);

  const fetchData = async (period: TimePeriod) => {
    if (!currentUserId) return;
    
    try {
      setLoading(true);
      let startDate: Date;
      const now = new Date();
      
      switch (period) {
        case 'today':
          startDate = startOfDay(now);
          break;
        case 'week':
          startDate = startOfWeek(now, { weekStartsOn: 0 });
          break;
        case 'month':
          startDate = startOfMonth(now);
          break;
      }

      const formattedStartDate = formatISO(startDate, { representation: 'date' });
      const formattedEndDate = formatISO(now, { representation: 'date' });
      const todayStr = formatISO(now, { representation: 'date' });
      
      console.log("Current date (client):", formatISO(now));
      console.log("Today's date as string:", todayStr);
      console.log("Fetching daily views from:", formattedStartDate, "to:", formattedEndDate);

      if (period === 'today') {
        const { data: todayViews, error: todayViewsError } = await supabase
          .from('daily_page_views')
          .select('view_count')
          .eq('user_id', currentUserId)
          .eq('view_date', todayStr)
          .single();

        if (todayViewsError && todayViewsError.code !== 'PGRST116') {
          console.error("Error fetching today's view data:", todayViewsError);
          toast({
            title: "Error fetching today's view data",
            description: todayViewsError.message,
            variant: "destructive",
          });
          setViewCount(0);
        } else {
          console.log("Today's views data received:", todayViews);
          setViewCount(todayViews?.view_count || 0);
        }
      } else if (period === 'week') {
        const weeklyViews = await getWeeklyPageViews(currentUserId);
        setViewCount(weeklyViews);
        console.log("Weekly page views:", weeklyViews);
      } else {
        const totalViews = await getTotalPageViews(currentUserId);
        setViewCount(totalViews);
        console.log("Total page views from daily_page_views:", totalViews);
      }

      const { data: dailyViews, error: dailyViewsError } = await supabase
        .from('daily_page_views')
        .select('view_date, view_count')
        .eq('user_id', currentUserId)
        .gte('view_date', formattedStartDate)
        .lte('view_date', formattedEndDate)
        .order('view_date', { ascending: true });

      if (dailyViewsError) {
        console.error("Error fetching daily view data:", dailyViewsError);
        toast({
          title: "Error fetching daily view data",
          description: dailyViewsError.message,
          variant: "destructive",
        });
      } else {
        console.log("Daily views data received:", dailyViews);
      }
      
      const { data: dailyRequests, error: dailyRequestsError } = await supabase
        .from('service_requests')
        .select('created_at')
        .eq('user_id', currentUserId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endOfDay(now).toISOString())
        .order('created_at', { ascending: true });

      if (dailyRequestsError) {
        console.error("Error fetching daily request data:", dailyRequestsError);
        toast({
          title: "Error fetching daily request data",
          description: dailyRequestsError.message,
          variant: "destructive",
        });
      }

      const { data: dailyPayments, error: dailyPaymentsError } = await supabase
        .from('service_requests')
        .select(`
          created_at,
          services (
            price
          )
        `)
        .eq('user_id', currentUserId)
        .eq('paid', true)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endOfDay(now).toISOString())
        .order('created_at', { ascending: true });

      if (dailyPaymentsError) {
        console.error("Error fetching daily payment data:", dailyPaymentsError);
        toast({
          title: "Error fetching daily payment data",
          description: dailyPaymentsError.message,
          variant: "destructive",
        });
      }

      const dateRange = getDateRange(period, now);
      
      const requestsByDate: Record<string, number> = {};
      dailyRequests?.forEach(request => {
        const dateStr = request.created_at.split('T')[0];
        requestsByDate[dateStr] = (requestsByDate[dateStr] || 0) + 1;
      });
      
      const revenueByDate: Record<string, number> = {};
      dailyPayments?.forEach(payment => {
        const dateStr = payment.created_at.split('T')[0];
        const price = payment.services?.price || 0;
        revenueByDate[dateStr] = (revenueByDate[dateStr] || 0) + price;
      });
      
      const viewsByDate: Record<string, number> = {};
      if (dailyViews && dailyViews.length > 0) {
        dailyViews.forEach(item => {
          viewsByDate[item.view_date] = item.view_count;
        });
      }
      
      console.log("Views by date mapping:", viewsByDate);
      console.log("Date range for chart:", dateRange.map(d => formatISO(d, { representation: 'date' })));
      console.log("Today's date for lookup:", todayStr);
      console.log("Today's view count in data:", viewsByDate[todayStr] || 0);
      
      const combinedChartData = dateRange.map((date) => {
        const dateStr = formatISO(date, { representation: 'date' });
        const formattedDate = format(date, 'dd');
        
        const viewCount = viewsByDate[dateStr] || 0;
        if (dateStr === todayStr) {
          console.log("Today's data in chart mapping:", dateStr, "Views:", viewCount);
        }
        
        const requestCount = requestsByDate[dateStr] || 0;
        const revenue = revenueByDate[dateStr] || 0;
        
        return {
          date: formattedDate,
          fullDate: format(date, 'EEE dd'),
          views: viewCount,
          requests: requestCount,
          revenue: revenue,
          key: `${dateStr}`
        };
      });
      
      console.log("Combined chart data:", combinedChartData);
      setChartData(combinedChartData);
      
      const hasAnyRevenueData = combinedChartData.some(item => item.revenue > 0);
      setHasRevenueData(hasAnyRevenueData);

      const { count: requestData, error: requestError } = await supabase
        .from('service_requests')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', currentUserId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endOfDay(now).toISOString());

      if (requestError) {
        console.error("Error fetching request count:", requestError);
      }

      const { data: paidRequests, error: paidRequestsError } = await supabase
        .from('service_requests')
        .select(`
          *,
          services (
            price
          )
        `)
        .eq('user_id', currentUserId)
        .eq('paid', true)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endOfDay(now).toISOString());

      if (paidRequestsError) {
        console.error("Error fetching paid requests:", paidRequestsError);
      } else {
        const totalSales = paidRequests?.reduce((sum, request) => {
          return sum + (request.services?.price || 0);
        }, 0) || 0;
        setSalesAmount(totalSales);
      }

      setRequestCount(requestData || 0);
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Error fetching dashboard data",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = (period: TimePeriod, now: Date): Date[] => {
    let startDate: Date;
    let endDate: Date = now;
    
    switch (period) {
      case 'today':
        return [now];
      case 'week':
        startDate = startOfWeek(now, { weekStartsOn: 0 });
        endDate = addDays(startDate, 6);
        break;
      case 'month':
        startDate = startOfMonth(now);
        break;
    }
    
    return eachDayOfInterval({ 
      start: startDate, 
      end: endDate 
    });
  };

  const handlePeriodChange = (period: TimePeriod) => {
    setSelectedPeriod(period);
    setLoading(true);
  };

  const refreshData = () => {
    if (currentUserId) {
      fetchData(selectedPeriod);
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-md shadow-sm">
          <p className="font-medium">{payload[0]?.payload.fullDate}</p>
          <p className="text-sm text-gebeya-pink">{t('dashboard.totalViews')}: {payload[0]?.value || 0}</p>
          <p className="text-sm text-gebeya-orange">{t('dashboard.serviceRequests')}: {payload[1]?.value || 0}</p>
        </div>
      );
    }
  
    return null;
  };

  const RevenueTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-md shadow-sm">
          <p className="font-medium">{payload[0]?.payload.fullDate}</p>
          <p className="text-sm text-gebeya-purple">{t('dashboard.revenue')}: {formatCurrency(payload[0]?.value || 0, userCurrency)}</p>
        </div>
      );
    }
  
    return null;
  };

  const formatXAxisTick = (value: string, index: number) => {
    if (isMobile && selectedPeriod === 'week' && chartData[index]) {
      const dayName = chartData[index].fullDate?.split(' ')[0];
      return dayName?.charAt(0) || value;
    }
    else if (isMobile && selectedPeriod === 'month') {
      return index % 5 === 0 ? value : '';
    }
    else if (selectedPeriod === 'week' && chartData[index]) {
      const dayName = chartData[index].fullDate?.split(' ')[0];
      return dayName || value;
    }
    return value;
  };

  const getChartHeight = () => {
    return isMobile ? 130 : 280;
  };

  const loadTopServices = async () => {
    try {
      setLoadingTopServices(true);
      const services = await fetchTopRequestedServices(3);
      setTopServices(services);
    } catch (error) {
      console.error("Error loading top services:", error);
    } finally {
      setLoadingTopServices(false);
    }
  };

  return (
    <div className="space-y-3 md:space-y-6">
      <div className="flex flex-row gap-2 md:gap-4 mb-3 md:mb-6">
        <Button
          variant={selectedPeriod === 'today' ? "default" : "outline"}
          size="sm"
          onClick={() => handlePeriodChange('today')}
          className={cn(
            "flex-1 md:flex-none",
            selectedPeriod === 'today' ? "bg-gebeya-pink hover:bg-gebeya-pink/90 text-white" : ""
          )}
        >
          {t('navigation.today')}
        </Button>
        <Button
          variant={selectedPeriod === 'week' ? "default" : "outline"}
          size="sm"
          onClick={() => handlePeriodChange('week')}
          className={cn(
            "flex-1 md:flex-none",
            selectedPeriod === 'week' ? "bg-gebeya-pink hover:bg-gebeya-pink/90 text-white" : ""
          )}
        >
          {t('dashboard.thisWeek')}
        </Button>
        <Button
          variant={selectedPeriod === 'month' ? "default" : "outline"}
          size="sm"
          onClick={() => handlePeriodChange('month')}
          className={cn(
            "flex-1 md:flex-none",
            selectedPeriod === 'month' ? "bg-gebeya-pink hover:bg-gebeya-pink/90 text-white" : ""
          )}
        >
          {t('dashboard.thisMonth')}
        </Button>
      </div>

      <div className="bg-white rounded-none md:rounded-xl shadow-sm border-x-0 md:border border-gray-100 px-2 py-3 md:p-6">
        <div className="grid grid-cols-3 gap-2 md:gap-6 mb-3 md:mb-6">
          <div className="bg-white rounded-lg py-2 px-1 md:py-3 md:px-3 transition-all hover:shadow-md border border-gray-100">
            <div className="flex flex-col items-center text-center">
              <Eye className="w-4 h-4 md:w-5 md:h-5 text-gebeya-pink mb-0.5 md:mb-1" />
              <div className="text-[10px] md:text-sm font-medium text-gray-500 mb-0.5 truncate w-full">
                <span className="hidden md:inline">{t('dashboard.totalViews')}</span>
                <span className="md:hidden">{t('dashboard.totalViews')}</span>
              </div>
              <div className="text-base md:text-xl font-semibold text-gray-900">
                {loading ? "-" : viewCount}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg py-2 px-1 md:py-3 md:px-3 transition-all hover:shadow-md border border-gray-100">
            <div className="flex flex-col items-center text-center">
              <ShoppingBag className="w-4 h-4 md:w-5 md:h-5 text-gebeya-orange mb-0.5 md:mb-1" />
              <div className="text-[10px] md:text-sm font-medium text-gray-500 mb-0.5 truncate w-full">
                <span className="hidden md:inline">{t('dashboard.serviceRequests')}</span>
                <span className="md:hidden">{t('dashboard.serviceRequests')}</span>
              </div>
              <div className="text-base md:text-xl font-semibold text-gray-900">
                {loading ? "-" : requestCount}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg py-2 px-1 md:py-3 md:px-3 transition-all hover:shadow-md border border-gray-100">
            <div className="flex flex-col items-center text-center">
              <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-gebeya-purple mb-0.5 md:mb-1" />
              <div className="text-[10px] md:text-sm font-medium text-gray-500 mb-0.5 truncate w-full">
                <span className="hidden md:inline">{t('dashboard.totalSales')}</span>
                <span className="md:hidden">{t('dashboard.totalSales')}</span>
              </div>
              <div className="text-base md:text-xl font-semibold text-gray-900">
                {loading ? "-" : formatCurrency(salesAmount, userCurrency)}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3 md:mt-8 border border-gray-100 rounded-lg md:rounded-xl px-2 py-2 md:p-6">
          <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-4">{t('dashboard.analyticsOverview')}</h3>
          {loading ? (
            <div className="h-40 md:h-64 flex items-center justify-center">
              <p className="text-gray-500">{t('dashboard.loadingData')}</p>
            </div>
          ) : chartData.length > 0 && (chartData.some(item => item.views > 0 || item.requests > 0)) ? (
            <div className={`h-[${getChartHeight()}px] w-full`}>
              <ResponsiveContainer width="100%" height={getChartHeight()}>
                <BarChart
                  data={chartData}
                  margin={{ 
                    top: 0, 
                    right: isMobile ? 0 : 30, 
                    left: isMobile ? 0 : 20, 
                    bottom: 0 
                  }}
                  barGap={0}
                  barCategoryGap={selectedPeriod === 'month' ? (isMobile ? 1 : 2) : (isMobile ? 1 : 8)}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: isMobile ? 9 : 12 }}
                    tickMargin={isMobile ? 3 : 10}
                    tickFormatter={formatXAxisTick}
                    interval={isMobile && selectedPeriod === 'month' ? 6 : 0}
                    axisLine={{ stroke: '#E5E7EB' }}
                  />
                  <YAxis 
                    tick={{ fontSize: isMobile ? 9 : 12 }} 
                    tickMargin={isMobile ? 2 : 10}
                    allowDecimals={false}
                    domain={[0, 'auto']}
                    width={isMobile ? 15 : 40}
                    axisLine={{ stroke: '#E5E7EB' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    wrapperStyle={{ fontSize: isMobile ? 9 : 12 }}
                    iconSize={isMobile ? 7 : 10}
                  />
                  <Bar
                    dataKey="views"
                    name={t('dashboard.pageViews')}
                    fill={gebeya.pink}
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={!isMobile}
                  />
                  <Bar
                    dataKey="requests"
                    name={t('dashboard.serviceRequests')}
                    fill={gebeya.orange}
                    radius={[4, 4, 0, 0]}
                    fillOpacity={0.8}
                    isAnimationActive={!isMobile}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-40 md:h-64 flex items-center justify-center">
              <p className="text-gray-500">{t('dashboard.noDataAvailable')}</p>
            </div>
          )}
        </div>

        {hasRevenueData && (
          <div className="mt-3 md:mt-8 border border-gray-100 rounded-lg md:rounded-xl px-2 py-2 md:p-6">
            <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-4">{t('dashboard.revenue')}</h3>
            {loading ? (
              <div className="h-40 md:h-64 flex items-center justify-center">
                <p className="text-gray-500">{t('dashboard.loadingData')}</p>
              </div>
            ) : (
              <div className={`h-[${getChartHeight()}px] w-full`}>
                <ResponsiveContainer width="100%" height={getChartHeight()}>
                  <AreaChart
                    data={chartData}
                    margin={{ 
                      top: 0, 
                      right: isMobile ? 0 : 30, 
                      left: isMobile ? 0 : 20, 
                      bottom: 0 
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: isMobile ? 9 : 12 }}
                      tickMargin={isMobile ? 3 : 10}
                      tickFormatter={formatXAxisTick}
                      interval={isMobile && selectedPeriod === 'month' ? 6 : 0}
                      axisLine={{ stroke: '#E5E7EB' }}
                    />
                    <YAxis 
                      tick={{ fontSize: isMobile ? 9 : 12 }} 
                      tickMargin={isMobile ? 2 : 10}
                      allowDecimals={false}
                      domain={[0, 'auto']}
                      width={isMobile ? 15 : 40}
                      axisLine={{ stroke: '#E5E7EB' }}
                    />
                    <Tooltip content={<RevenueTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      name={`${t('dashboard.revenue')} (${userCurrency})`}
                      fill={gebeya.purple}
                      stroke={gebeya.purple}
                      fillOpacity={0.3}
                      isAnimationActive={!isMobile}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-none md:rounded-xl shadow-sm border-x-0 md:border border-gray-100 px-2 py-3 md:p-6">
        <div className="flex items-center justify-between mb-2 md:mb-4">
          <h3 className="text-base md:text-lg font-semibold">{t('dashboard.topRequestedServices')}</h3>
        </div>
        <div className="space-y-2 md:space-y-4">
          {loadingTopServices ? (
            <div className="py-8 flex items-center justify-center">
              <p className="text-gray-500">{t('dashboard.loadingData')}</p>
            </div>
          ) : topServices.length > 0 ? (
            topServices.map((service) => (
              <div key={service.id} className="flex items-center gap-3 p-4 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                <ChartBar className="w-5 h-5 text-gray-400" />
                <div className="flex-1">
                  <span className="text-gray-900 font-medium">{service.name}</span>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-sm text-gray-500">
                      {service.requestCount} {service.requestCount === 1 ? 'request' : 'requests'}
                    </span>
                    <span className="text-sm font-medium">
                      {formatCurrency(service.price, userCurrency)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="py-6 text-center text-gray-500">
              <p>{t('dashboard.noServiceRequestsYet')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
